import { useState } from "react";
import { Link } from "../lib/router";
import { RevealSection } from "../components/LandingAnimations";

const TIERS = [
  {
    name: "Free",
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: "Perfect for getting started",
    features: [
      "3 social accounts",
      "30 posts per month",
      "Basic analytics",
      "1 project",
      "Community support",
    ],
    cta: "Get Started",
    href: "/register",
    highlighted: false,
  },
  {
    name: "Pro",
    monthlyPrice: 19,
    yearlyPrice: 15,
    description: "For creators who mean business",
    features: [
      "15 social accounts",
      "Unlimited posts",
      "Advanced analytics & insights",
      "5 projects",
      "API access",
      "Priority support",
      "Platform overrides",
      "Custom date ranges",
    ],
    cta: "Start Free Trial",
    href: "/register",
    highlighted: true,
    badge: "Most Popular",
  },
  {
    name: "Enterprise",
    monthlyPrice: null,
    yearlyPrice: null,
    description: "For teams and agencies",
    features: [
      "Unlimited accounts",
      "Unlimited posts",
      "Custom analytics",
      "Unlimited projects",
      "Dedicated API",
      "SLA guarantee",
      "Dedicated support",
      "Custom integrations",
    ],
    cta: "Contact Sales",
    href: "/register",
    highlighted: false,
  },
];

const COMPARISON = [
  { feature: "Social accounts", free: "3", pro: "15", enterprise: "Unlimited" },
  { feature: "Monthly posts", free: "30", pro: "Unlimited", enterprise: "Unlimited" },
  { feature: "Projects", free: "1", pro: "5", enterprise: "Unlimited" },
  { feature: "Analytics", free: "Basic", pro: "Advanced", enterprise: "Custom" },
  { feature: "Page insights", free: false, pro: true, enterprise: true },
  { feature: "API access", free: false, pro: true, enterprise: true },
  { feature: "Platform overrides", free: false, pro: true, enterprise: true },
  { feature: "Priority support", free: false, pro: true, enterprise: true },
  { feature: "Custom integrations", free: false, pro: false, enterprise: true },
  { feature: "SLA guarantee", free: false, pro: false, enterprise: true },
];

const FAQS = [
  {
    q: "Can I switch plans anytime?",
    a: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any differences.",
  },
  {
    q: "Is there a free trial for Pro?",
    a: "Yes! Pro comes with a 14-day free trial. No credit card required to start.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit cards (Visa, Mastercard, Amex) and PayPal. Enterprise plans can be invoiced.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Absolutely. There are no long-term contracts. Cancel anytime and you'll retain access until the end of your billing period.",
  },
  {
    q: "Do you offer discounts for nonprofits?",
    a: "Yes! We offer 50% off Pro plans for registered nonprofits and educational institutions. Contact us to apply.",
  },
];

export function Pricing() {
  const [yearly, setYearly] = useState(false);

  return (
    <>
      {/* Hero */}
      <section className="pt-20 pb-8 px-4 text-center">
        <div className="animate-fade-up">
          <span className="inline-block px-3 py-1 rounded-full bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-semibold border border-orange-200/60 dark:border-orange-500/20 mb-6">
            Pricing
          </span>
        </div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-4 animate-fade-up" style={{ animationDelay: "0.1s" }}>
          Simple, transparent <span className="text-gradient">pricing</span>
        </h1>
        <p className="text-lg text-stone-500 dark:text-zinc-400 max-w-md mx-auto mb-8 animate-fade-up" style={{ animationDelay: "0.2s" }}>
          Start free. Scale as you grow. No hidden fees.
        </p>

        {/* Toggle */}
        <div className="flex items-center justify-center gap-3 animate-fade-up" style={{ animationDelay: "0.3s" }}>
          <span className={`text-sm font-medium ${!yearly ? "text-stone-900 dark:text-white" : "text-stone-400 dark:text-zinc-500"}`}>Monthly</span>
          <button
            onClick={() => setYearly(!yearly)}
            className={`relative w-12 h-6 rounded-full transition-colors ${yearly ? "bg-orange-500" : "bg-stone-300 dark:bg-zinc-600"}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${yearly ? "translate-x-6" : "translate-x-0.5"}`} />
          </button>
          <span className={`text-sm font-medium ${yearly ? "text-stone-900 dark:text-white" : "text-stone-400 dark:text-zinc-500"}`}>
            Yearly
            <span className="ml-1.5 px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 text-[10px] font-bold">-20%</span>
          </span>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12 px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
          {TIERS.map((tier, i) => (
            <RevealSection key={tier.name} className="h-full flex">
              <div className={`w-full rounded-2xl border p-6 h-full flex flex-col transition-all duration-300 ${
                tier.highlighted
                  ? "border-orange-300 dark:border-orange-500/40 bg-white dark:bg-zinc-900 shadow-xl shadow-orange-500/5 relative"
                  : "border-stone-200/60 dark:border-zinc-800/60 bg-white dark:bg-zinc-900 hover:shadow-lg"
              }`}>
                {tier.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-orange-500 text-white text-xs font-bold shadow-lg">
                    {tier.badge}
                  </span>
                )}

                <div className="mb-6 min-h-[64px]">
                  <h3 className="text-lg font-bold text-stone-900 dark:text-white">{tier.name}</h3>
                  <p className="text-sm text-stone-400 dark:text-zinc-500 mt-1">{tier.description}</p>
                </div>

                <div className="mb-6 min-h-[72px]">
                  {tier.monthlyPrice !== null ? (
                    <>
                      <span className="text-4xl font-extrabold text-stone-900 dark:text-white">
                        ${yearly ? tier.yearlyPrice : tier.monthlyPrice}
                      </span>
                      <span className="text-stone-400 dark:text-zinc-500 text-sm ml-1">
                        {tier.monthlyPrice === 0 ? "forever" : "/month"}
                      </span>
                      {yearly && tier.yearlyPrice! > 0 && (
                        <p className="text-xs text-stone-400 dark:text-zinc-500 mt-1">
                          ${tier.yearlyPrice! * 12}/year &middot; billed annually
                        </p>
                      )}
                    </>
                  ) : (
                    <span className="text-4xl font-extrabold text-stone-900 dark:text-white">Custom</span>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-stone-600 dark:text-zinc-300">
                      <span className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">&#10003;</span>
                      {f}
                    </li>
                  ))}
                </ul>

                <Link
                  href={tier.href}
                  className={`block text-center py-3 rounded-xl font-semibold text-sm transition-all ${
                    tier.highlighted
                      ? "bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/20"
                      : "bg-stone-100 dark:bg-zinc-800 text-stone-700 dark:text-zinc-200 hover:bg-stone-200 dark:hover:bg-zinc-700"
                  }`}
                >
                  {tier.cta}
                </Link>
              </div>
            </RevealSection>
          ))}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-4">
        <RevealSection className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-extrabold text-stone-900 dark:text-white text-center mb-8">
            Compare plans
          </h2>
          <div className="rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 bg-white dark:bg-zinc-900 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200/60 dark:border-zinc-800/60">
                  <th className="text-left p-4 font-semibold text-stone-900 dark:text-white">Feature</th>
                  <th className="p-4 text-center font-semibold text-stone-900 dark:text-white">Free</th>
                  <th className="p-4 text-center font-semibold text-orange-600 dark:text-orange-400">Pro</th>
                  <th className="p-4 text-center font-semibold text-stone-900 dark:text-white">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.feature} className="border-b border-stone-100 dark:border-zinc-800/40 last:border-0">
                    <td className="p-4 text-stone-600 dark:text-zinc-300">{row.feature}</td>
                    {[row.free, row.pro, row.enterprise].map((v, i) => (
                      <td key={i} className="p-4 text-center">
                        {typeof v === "boolean" ? (
                          v ? (
                            <span className="inline-flex w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 items-center justify-center text-xs">&#10003;</span>
                          ) : (
                            <span className="text-stone-300 dark:text-zinc-700">&mdash;</span>
                          )
                        ) : (
                          <span className="text-stone-700 dark:text-zinc-300 font-medium">{v}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </RevealSection>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-stone-50 dark:bg-zinc-900/50 px-4">
        <RevealSection className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-extrabold text-stone-900 dark:text-white text-center mb-10">
            Frequently asked questions
          </h2>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <FaqItem key={i} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </RevealSection>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/[0.06] to-transparent dark:from-orange-500/[0.04]" />
        <RevealSection className="relative text-center max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-4">
            Ready to get started?
          </h2>
          <p className="text-stone-500 dark:text-zinc-400 mb-8">
            Start publishing to all your platforms today. Free forever.
          </p>
          <Link href="/register" className="inline-block px-8 py-4 bg-orange-500 text-white rounded-2xl font-semibold text-lg hover:bg-orange-600 transition-all animate-pulse-glow shadow-lg shadow-orange-500/20">
            Start Publishing Free
          </Link>
          <p className="text-xs text-stone-400 dark:text-zinc-600 mt-4">No credit card required</p>
        </RevealSection>
      </section>
    </>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-stone-200/60 dark:border-zinc-800/60 bg-white dark:bg-zinc-900 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <span className="text-sm font-semibold text-stone-900 dark:text-white">{question}</span>
        <svg className={`w-4 h-4 text-stone-400 dark:text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-40" : "max-h-0"}`}>
        <p className="px-4 pb-4 text-sm text-stone-500 dark:text-zinc-400 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}
